import React, { useMemo } from "react";

/**
 * Audit lane for the chart area.
 *
 * Every automation and AI decision is rendered as a tick on a shared time
 * axis, including the ones that never became an order. Those are the majority
 * and were previously invisible anywhere in the UI: over one week the agent
 * produced 1,103 WAITs and 353 blocked entries against 19 executions, and the
 * only way to see that was to query the database directly.
 *
 * Ticks are positioned by timestamp across the supplied window so the lane can
 * sit directly beneath the price chart and read as the same timeline.
 */

const KIND_STYLES = {
  entry: { dot: "bg-emerald-400", ring: "ring-emerald-400/40", label: "Entry" },
  exit: { dot: "bg-sky-400", ring: "ring-sky-400/40", label: "Exit" },
  blocked: { dot: "bg-red-400", ring: "ring-red-400/40", label: "Blocked" },
  error: { dot: "bg-orange-400", ring: "ring-orange-400/40", label: "Error" },
  hold: { dot: "bg-amber-300", ring: "ring-amber-300/40", label: "Hold" },
  signal: { dot: "bg-violet-400", ring: "ring-violet-400/40", label: "Signal" },
  wait: { dot: "bg-zinc-500", ring: "ring-zinc-500/30", label: "Wait" },
};

const KIND_ORDER = ["entry", "exit", "blocked", "error", "hold", "signal", "wait"];

const styleFor = (kind) => KIND_STYLES[kind] || KIND_STYLES.wait;

const formatClock = (iso) => {
  if (!iso) return "";
  const parsed = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const DecisionLane = ({
  decisions = [],
  counts = {},
  windowHours = 24,
  loading = false,
  error = null,
  onSelect,
  defaultExpanded = false,
}) => {
  // Collapsed by default: the lane is valuable but it must not permanently
  // cost the chart vertical space. Squeezing the chart shrinks candle bodies
  // relative to their wicks, which is the first thing a trader notices.
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const { ticks, span } = useMemo(() => {
    const usable = (decisions || []).filter((item) => item && item.at);
    if (!usable.length) return { ticks: [], span: null };

    const times = usable.map((item) => {
      const iso = item.at.endsWith("Z") ? item.at : `${item.at}Z`;
      return new Date(iso).getTime();
    });
    const first = Math.min(...times);
    const last = Math.max(...times);
    const width = Math.max(1, last - first);

    return {
      span: { first, last },
      ticks: usable.map((item, index) => ({
        item,
        // Percent position across the window keeps the lane aligned with a
        // time axis without needing the chart's internal scale.
        left: ((times[index] - first) / width) * 100,
      })),
    };
  }, [decisions]);

  const legend = KIND_ORDER.filter((kind) => counts[kind]);

  return (
    <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/60">
            Decisions
          </span>
          <span className="text-[10px] text-white/35">last {windowHours}h</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-white/50 transition hover:bg-white/5 hover:text-white/80"
          >
            {expanded ? "Hide" : "Show"}
          </button>
          {legend.map((kind) => (
            <span key={kind} className="flex items-center gap-1 text-[10px] text-white/50">
              <span className={`h-1.5 w-1.5 rounded-full ${styleFor(kind).dot}`} />
              {styleFor(kind).label}
              <span className="text-white/35">{counts[kind]}</span>
            </span>
          ))}
        </div>
      </div>

      {expanded ? (
      <>
      <div className="relative mt-2 h-8">
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-white/10" />

        {loading && !ticks.length ? (
          <div className="absolute inset-0 flex items-center text-[10px] text-white/35">
            Loading decision history…
          </div>
        ) : null}

        {error ? (
          <div className="absolute inset-0 flex items-center text-[10px] text-red-300/80">
            Decision history unavailable — {error}
          </div>
        ) : null}

        {!loading && !error && !ticks.length ? (
          <div className="absolute inset-0 flex items-center text-[10px] text-white/35">
            No decisions recorded in this window for the selected account and symbol.
          </div>
        ) : null}

        {ticks.map(({ item, left }) => {
          const style = styleFor(item.kind);
          const detail = [
            `${style.label}${item.action ? ` · ${item.action}` : ""}`,
            item.symbol || null,
            item.source === "ai" ? "AI" : "Automation",
            formatClock(item.at),
            item.reason_code || null,
            item.reason || null,
            item.rationale || null,
          ]
            .filter(Boolean)
            .join("\n");

          return (
            <button
              key={`${item.source}-${item.id}`}
              type="button"
              title={detail}
              onClick={() => onSelect?.(item)}
              aria-label={detail}
              style={{ left: `${left}%` }}
              className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-transform hover:scale-150 focus:outline-none focus:ring-2 ${style.ring} ${
                item.source === "ai" ? "h-2.5 w-2.5" : "h-1.5 w-1.5"
              } ${style.dot}`}
            />
          );
        })}
      </div>

      {span ? (
        <div className="flex justify-between text-[9px] text-white/30">
          <span>{formatClock(new Date(span.first).toISOString())}</span>
          <span>{formatClock(new Date(span.last).toISOString())}</span>
        </div>
      ) : null}
      </>
      ) : null}
    </div>
  );
};

export default DecisionLane;
